import { useCallback, useMemo } from 'react';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import { useShowTagSelectorDropdown } from '@/hooks/useShowTagSelectorDropdown';
import { useGetUserTagsQuery } from '@/store/usersApi';
import {
  buildAccountTagOptions,
  type AccountTagOption,
} from '@/modules/portfolio/accountTags/accountTagCatalog';

/**
 * Loads the entitlement tag catalog (GET /api/user/tags) used by account
 * tag assignment. Options are tags the user can assign, and are empty when
 * the tag selector is unavailable.
 */
export function useAccountTagCatalogOptions(): {
  options: AccountTagOption[];
  userTagIdSet: Set<number>;
  isLoading: boolean;
  refetchUserTags: () => void;
} {
  const authLoaded = useAuthIsLoaded();
  const showTagSelector = useShowTagSelectorDropdown();

  const {
    data: accessibleUserTags = [],
    isLoading: isLoadingUserTags,
    refetch: refetchUserTags,
  } = useGetUserTagsQuery(undefined, {
    skip: !showTagSelector || !authLoaded,
  });

  const options = useMemo(
    () => buildAccountTagOptions(accessibleUserTags),
    [accessibleUserTags],
  );

  const userTagIdSet = useMemo(
    () => new Set(options.map((tag) => tag.value)),
    [options],
  );

  const isLoading =
    (showTagSelector && !authLoaded) || (showTagSelector && isLoadingUserTags);

  const refetchUserTagsIfAllowed = useCallback(() => {
    if (showTagSelector && authLoaded) {
      void refetchUserTags();
    }
  }, [showTagSelector, authLoaded, refetchUserTags]);

  return {
    options,
    userTagIdSet,
    isLoading,
    refetchUserTags: refetchUserTagsIfAllowed,
  };
}
